import { useLatest } from 'ahooks';
import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useFramework } from '@/framework';
import { registerStyleSheet } from '@/utils/common-kit';
import { LayerType, type Cell, type ITableLayer, type RowOrColSelectedRectInfo } from '@/version-types';
import addOrUpdateLayer from '../../utils/add-or-update-layer';
import { getTableClipboardData, pasteToTableLayer } from '../../utils/clipboard';
import { useTableEditState, type CellsSelectedInfo } from './state';
import { TableAction } from './table-action';

export const useActiveTableLayer = () => {
    // --- framework hooks ---
    const framework = useFramework();

    // --- internal states ---
    const [targetTableLayer, setTargetTableLayer] = useState<ITableLayer | null>(null);

    // --- internal refs ---
    const targetLayerIdRef = useLatest(targetTableLayer?.id ?? null);

    useEffect(() => {
        const activeUpdateListenerID = framework.service.frame.onActiveLayerDataUpdate((activeLayer, pickLayer) => {
            const getTargetTableLayer = (): ITableLayer | null => {
                if (!activeLayer) return null;

                let maybeTargetLayer = activeLayer;
                if (activeLayer.layerType === LayerType.GROUP_LAYER && pickLayer) {
                    maybeTargetLayer = pickLayer;
                }

                if (maybeTargetLayer.layerType !== LayerType.TABLE) return null;
                return maybeTargetLayer as ITableLayer;
            };

            setTargetTableLayer(getTargetTableLayer());
        });

        const baseOperateListenerID = framework.service.frame.onBaseOperate((layerId) => {
            if (layerId !== targetLayerIdRef.current) return;

            const layer = framework.service.flatLayer.getLayer(layerId);
            setTargetTableLayer(layer as ITableLayer);
        });

        return () => {
            framework.service.events.off(activeUpdateListenerID);
            framework.service.events.off(baseOperateListenerID);
        };
    }, []);

    useEffect(() => {
        const layerId = targetTableLayer?.id;
        if (!layerId) return;

        const layerChangeListenerID = framework.service.flatLayer.onChange((layer) => {
            if (!layer || layer.layerType !== LayerType.TABLE) return;
            if (layer.id !== targetLayerIdRef.current) return;
            setTargetTableLayer(layer as ITableLayer);
        }, layerId);

        return () => {
            framework.service.events.off(layerChangeListenerID);
        };
    }, [targetTableLayer?.id]);

    return targetTableLayer;
};

export const useTableEditNodeCursorCss = ({
    tableEditNode,
    isActiveTableLayer,
    isDisableMoveState,
}: {
    tableEditNode: HTMLElement | null;
    isActiveTableLayer: boolean;
    isDisableMoveState: boolean;
}) => {
    const lineIsDragging = useTableEditState()((s) => s.lineIsDragging);
    const cssClassName = useState(() => `css-${uuid()}`)[0];

    useEffect(() => {
        if (!tableEditNode) return;
        tableEditNode.classList.add(cssClassName);
        return () => {
            tableEditNode.classList.remove(cssClassName);
        };
    }, [tableEditNode]);

    useEffect(() => {
        if (!isActiveTableLayer) return;
        if (isDisableMoveState) return;
        if (lineIsDragging) return;
        const removeStyleSheet = registerStyleSheet(`
            .${cssClassName} {
                cursor: var(--cursor-move) !important;
            }
        `);
        return removeStyleSheet;
    }, [isActiveTableLayer, isDisableMoveState, lineIsDragging]);
};

export const useTextBizHotKey = () => {
    const framework = useFramework();
    const { getState } = useTableEditState();
    const activeTableLayerRef = useLatest(useActiveTableLayer());

    useEffect(() => {
        const doDelete = (
            layer: ITableLayer,
            selectedInfo: {
                cellsSelectedInfo: CellsSelectedInfo | null;
                rowOrColSelectedRectInfo: RowOrColSelectedRectInfo | null;
            },
        ) => {
            const { cellsSelectedInfo, rowOrColSelectedRectInfo } = selectedInfo;
            const hasSelectedInfo = !!cellsSelectedInfo || !!rowOrColSelectedRectInfo;
            if (!hasSelectedInfo) return;

            let newTableLayer = cloneDeep(layer);
            let cellInfoList: Record<'vIndex' | 'hIndex', number>[] = [];
            if (cellsSelectedInfo) {
                cellInfoList = cellsSelectedInfo.cellInfoList;
            } else if (rowOrColSelectedRectInfo) {
                cellInfoList =
                    rowOrColSelectedRectInfo.direction === 'horizontal'
                        ? (newTableLayer.data.cells[rowOrColSelectedRectInfo.index].filter(Boolean) as Cell[])
                        : (newTableLayer.data.cells
                              .map((row) => row[rowOrColSelectedRectInfo.index])
                              .filter(Boolean) as Cell[]);
            }
            for (const cellInfo of cellInfoList) {
                const cell = newTableLayer.data.cells[cellInfo.vIndex]?.[cellInfo.hIndex];
                if (cell && cell.content.length) {
                    cell.content = '';
                }
            }

            newTableLayer = addOrUpdateLayer({
                frameworkIdentity: framework.identity,
                layer: newTableLayer,
                resetMaterialIds: true,
            });
            framework.service.frame.setActiveLayer(newTableLayer.id);
            framework.service.history.write(cloneDeep(layer), newTableLayer);

            new TableAction(framework.identity).emit('delete');
        };

        const copyListenerID = framework.service.hotkey.onCopy(() => {
            if (!activeTableLayerRef.current) return false;
            const { cellsSelectedInfo, rowOrColSelectedRectInfo, hasEditingText } = getState();
            if (hasEditingText) return false;
            const hasSelectedInfo = !!cellsSelectedInfo || !!rowOrColSelectedRectInfo;
            if (!hasSelectedInfo) return false;

            getTableClipboardData({
                frameworkIdentity: framework.identity,
                layerId: activeTableLayerRef.current.id,
                selectedInfo: {
                    cellsSelectedInfo,
                    rowOrColSelectedRectInfo,
                },
            }).then((clipboardData) => {
                if (!clipboardData) return;
                navigator.clipboard.write(clipboardData);
            });

            return true;
        }, 9);
        const cutListenerID = framework.service.hotkey.onCut(() => {
            if (!activeTableLayerRef.current) return false;
            const { cellsSelectedInfo, rowOrColSelectedRectInfo, hasEditingText } = getState();
            if (hasEditingText) return false;
            const hasSelectedInfo = !!cellsSelectedInfo || !!rowOrColSelectedRectInfo;
            if (!hasSelectedInfo) return false;

            getTableClipboardData({
                frameworkIdentity: framework.identity,
                layerId: activeTableLayerRef.current.id,
                selectedInfo: {
                    cellsSelectedInfo,
                    rowOrColSelectedRectInfo,
                },
            }).then((clipboardData) => {
                if (!clipboardData) return;
                navigator.clipboard.write(clipboardData);
            });
            doDelete(activeTableLayerRef.current, {
                cellsSelectedInfo,
                rowOrColSelectedRectInfo,
            });

            return true;
        }, 9);
        const pasteListenerID = framework.service.hotkey.onPaste(() => {
            if (!activeTableLayerRef.current) return false;
            const { cellsSelectedInfo, hasEditingText } = getState();
            if (hasEditingText) return false;
            if (!cellsSelectedInfo?.cellInfoList?.length) return false;

            pasteToTableLayer({
                frameworkIdentity: framework.identity,
                layerId: activeTableLayerRef.current.id,
                cellsSelectedInfo: cellsSelectedInfo,
            });

            new TableAction(framework.identity).emit('paste');

            return true;
        }, 9);
        const deleteListenerID = framework.service.hotkey.onDelete((args) => {
            // 非物理键盘触发时走默认删除逻辑（菜单/快捷方式等）
            if (args?.from !== 'keyboard') return false;

            if (!activeTableLayerRef.current) return false;
            const { cellsSelectedInfo, rowOrColSelectedRectInfo, hasEditingText } = getState();
            if (hasEditingText) return false;
            const hasSelectedInfo = !!cellsSelectedInfo || !!rowOrColSelectedRectInfo;
            if (!hasSelectedInfo) return false;

            doDelete(activeTableLayerRef.current, {
                cellsSelectedInfo,
                rowOrColSelectedRectInfo,
            });

            return true;
        }, 9);

        return () => {
            framework.service.events.off(copyListenerID);
            framework.service.events.off(cutListenerID);
            framework.service.events.off(pasteListenerID);
            framework.service.events.off(deleteListenerID);
        };
    }, []);
};
